import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Map,
  BookOpen,
  Star,
  MessageSquare,
  Calendar,
  Euro,
  Eye,
  Users,
  MousePointer,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  TrendingUp,
  BarChart3,
  Chrome,
  Apple,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentReservations, setRecentReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, reservationsRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, getAuthHeaders()),
          axios.get(`${API}/admin/reservations`, getAuthHeaders()),
          axios.get(`${API}/analytics/summary?days=30`, getAuthHeaders()).catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setRecentReservations(reservationsRes.data.slice(0, 5));
        setAnalytics(analyticsRes.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const statCards = [
    {
      title: "Circuits",
      value: stats?.circuits || 0,
      icon: Map,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      link: "/admin/circuits",
    },
    {
      title: "Réservations",
      value: stats?.reservations || 0,
      icon: BookOpen,
      color: "text-green-600",
      bgColor: "bg-green-100",
      link: "/admin/reservations",
    },
    {
      title: "En attente",
      value: stats?.pending_reservations || 0,
      icon: Calendar,
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
      link: "/admin/reservations",
    },
    {
      title: "Témoignages",
      value: stats?.testimonials || 0,
      icon: Star,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      link: "/admin/testimonials",
    },
    {
      title: "Messages",
      value: stats?.contacts || 0,
      icon: MessageSquare,
      color: "text-red-600",
      bgColor: "bg-red-100",
      link: "/admin/contacts",
    },
    {
      title: "Revenus",
      value: `€${(stats?.total_revenue || 0).toFixed(0)}`,
      icon: Euro,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
      link: "/admin/reservations",
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "confirmed": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return Smartphone;
      case 'tablet': return Tablet;
      default: return Monitor;
    }
  };

  const getBrowserIcon = (browser) => {
    if (browser === 'Safari') return Apple;
    return Chrome;
  };

  // Calculate percentages for device breakdown
  const totalDevices = analytics ? Object.values(analytics.device_breakdown || {}).reduce((a, b) => a + b, 0) : 0;
  const devicePercentages = analytics?.device_breakdown 
    ? Object.entries(analytics.device_breakdown).map(([device, count]) => ({
        device,
        count,
        percentage: totalDevices > 0 ? Math.round((count / totalDevices) * 100) : 0
      }))
    : [];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Vue d'ensemble de votre activité</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                    <p className="text-2xl font-bold">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Aperçu</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {/* Recent Reservations */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Réservations récentes</CardTitle>
              <Link to="/admin/reservations" className="text-sm text-primary hover:underline">
                Voir tout
              </Link>
            </CardHeader>
            <CardContent>
              {recentReservations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Aucune réservation</p>
              ) : (
                <div className="space-y-4">
                  {recentReservations.map((reservation) => (
                    <div
                      key={reservation.id}
                      className="flex items-center justify-between p-4 bg-muted rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{reservation.user_name || reservation.customer_name || 'N/A'}</p>
                        <p className="text-sm text-muted-foreground truncate">{reservation.user_email || reservation.customer_email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {reservation.preferred_date || reservation.travel_date} • {reservation.num_travelers || reservation.num_participants || 1} pers.
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-semibold text-primary">€{reservation.total_price?.toFixed(2) || '0.00'}</p>
                        <span className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${getStatusColor(reservation.status)}`}>
                          {reservation.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          {!analytics ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Les données analytics apparaîtront après les premières visites sur le site</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Analytics Overview Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <Eye className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Visites totales</p>
                        <p className="text-2xl font-bold">{analytics.total_visits || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <Users className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Visiteurs uniques</p>
                        <p className="text-2xl font-bold">{analytics.total_unique_visitors || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-purple-100">
                        <MousePointer className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Clics totaux</p>
                        <p className="text-2xl font-bold">{analytics.total_clicks || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-orange-100">
                        <TrendingUp className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Visites aujourd'hui</p>
                        <p className="text-2xl font-bold">{analytics.visits_today || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Time Period Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Cette semaine</p>
                    <p className="text-3xl font-bold text-primary">{analytics.visits_this_week || 0}</p>
                    <p className="text-xs text-muted-foreground">visites</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Ce mois</p>
                    <p className="text-3xl font-bold text-primary">{analytics.visits_this_month || 0}</p>
                    <p className="text-xs text-muted-foreground">visites</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">30 derniers jours</p>
                    <p className="text-3xl font-bold text-primary">{analytics.total_visits || 0}</p>
                    <p className="text-xs text-muted-foreground">visites</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Device Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Monitor className="h-5 w-5" />
                      Appareils
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {devicePercentages.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">Pas de données</p>
                    ) : (
                      <div className="space-y-4">
                        {devicePercentages.map(({ device, count, percentage }) => {
                          const Icon = getDeviceIcon(device);
                          return (
                            <div key={device} className="flex items-center gap-4">
                              <Icon className="h-5 w-5 text-muted-foreground" />
                              <div className="flex-1">
                                <div className="flex justify-between mb-1">
                                  <span className="capitalize">{device}</span>
                                  <span className="text-muted-foreground">{count} ({percentage}%)</span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-primary rounded-full transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Countries */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5" />
                      Pays
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!analytics.top_countries || analytics.top_countries.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">Pas de données de localisation</p>
                    ) : (
                      <div className="space-y-3">
                        {analytics.top_countries.slice(0, 5).map((country, index) => (
                          <div key={country.country} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{index + 1}.</span>
                              <span>{country.country}</span>
                            </div>
                            <span className="font-semibold">{country.visits} visites</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Pages */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Pages les plus visitées
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!analytics.top_pages || analytics.top_pages.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">Pas de données</p>
                    ) : (
                      <div className="space-y-3">
                        {analytics.top_pages.slice(0, 5).map((page, index) => (
                          <div key={page.page} className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-lg text-muted-foreground">{index + 1}.</span>
                              <span className="truncate">{page.page === '/' ? 'Accueil' : page.page}</span>
                            </div>
                            <span className="font-semibold ml-2">{page.visits}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Browser Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Chrome className="h-5 w-5" />
                      Navigateurs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!analytics.browser_breakdown || Object.keys(analytics.browser_breakdown).length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">Pas de données</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(analytics.browser_breakdown).map(([browser, count]) => (
                          <div key={browser} className="flex items-center justify-between">
                            <span>{browser}</span>
                            <span className="font-semibold">{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* OS Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Monitor className="h-5 w-5" />
                      Systèmes d'exploitation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!analytics.os_breakdown || Object.keys(analytics.os_breakdown).length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">Pas de données</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(analytics.os_breakdown).map(([os, count]) => (
                          <div key={os} className="flex items-center justify-between">
                            <span>{os}</span>
                            <span className="font-semibold">{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Referrers */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Sources de trafic
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!analytics.top_referrers || analytics.top_referrers.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">Trafic direct uniquement</p>
                    ) : (
                      <div className="space-y-3">
                        {analytics.top_referrers.slice(0, 5).map((ref, index) => (
                          <div key={ref.referrer} className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-lg text-muted-foreground">{index + 1}.</span>
                              <span className="truncate text-sm">{ref.referrer}</span>
                            </div>
                            <span className="font-semibold ml-2">{ref.visits}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
